import { formatForDisplay } from "../utils/gmtUtils";
import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  TrashIcon,
  DocumentMagnifyingGlassIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import Pagination from "../components/Pagination";
import DocumentDetailsSidebar from "../components/DocumentDetailsSidebar";
import PermissionGuard from "../components/common/PermissionGuard";
import PagePermissionGuard from "../components/common/PagePermissionGuard";
import { DataGrid, EmptyDocumentsIllustration } from "../components/ui";

const AllDocuments = () => {
  const {
    signRequests,
    handleDeleteClick,
    handleResendClick,
    pillColors,
    statusTextMapped,
    currentPage,
    totalPages,
    handlePageChange,
    sortBy,
    sortOrder,
    handleSortChange,
    handleDocumentUpdated,
  } = useOutletContext();
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const handleDetailsClick = (doc) => {
    setSelectedDocument(doc);
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedDocument(null);
  };

  const getStatusBadge = (doc) => {
    const rawStatus = doc.status;
    const normalizedStatus = rawStatus
      ? String(rawStatus).trim().toUpperCase()
      : null;
    const statusColor =
      normalizedStatus && pillColors?.[normalizedStatus]
        ? pillColors[normalizedStatus]
        : "bg-gray-500 text-white text-xs";
    const statusText =
      normalizedStatus && statusTextMapped?.[normalizedStatus]
        ? statusTextMapped[normalizedStatus]
        : normalizedStatus || "Unknown";
    const getStatusStyle = () => {
      if (normalizedStatus === "IN_PROGRESS")
        return { backgroundColor: "#eab308", color: "#ffffff" };
      if (normalizedStatus === "COMPLETED")
        return { backgroundColor: "#22c55e", color: "#ffffff" };
      if (normalizedStatus === "EXPIRED")
        return { backgroundColor: "#ef4444", color: "#ffffff" };
      if (normalizedStatus === "PENDING")
        return { backgroundColor: "#6b7280", color: "#ffffff" };
      if (normalizedStatus === "ARCHIVED")
        return { backgroundColor: "#6b7280", color: "#ffffff" };
      if (normalizedStatus === "DECLINED")
        return { backgroundColor: "#dc2626", color: "#ffffff" };
      if (normalizedStatus === "SENT_EMAIL")
        return { backgroundColor: "#3b82f6", color: "#ffffff" };
      return { backgroundColor: "#6b7280", color: "#ffffff" };
    };
    return (
      <span
        className={`px-3 py-1 inline-flex leading-5 rounded ${statusColor}`}
        style={getStatusStyle()}
      >
        {statusText}
      </span>
    );
  };

  const columns = [
    {
      key: "name",
      label: "Document Name",
      sortable: true,
      render: (_, row) => (
        <div className="text-sm font-medium text-gray-900 max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">
          {row.name}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (_, row) => getStatusBadge(row),
    },
    {
      key: "signers",
      label: "Signers",
      sortable: false,
      render: (_, row) => (
        <span className="text-sm text-gray-500">
          {`${row.recipients?.filter((r) => !r.isCC).length ?? 0} signer${(row.recipients?.filter((r) => !r.isCC).length ?? 0) > 1 ? "s" : ""}`}
          {(row.recipients?.filter((r) => r.isCC).length ?? 0) > 0 &&
            `, ${row.recipients?.filter((r) => r.isCC).length} cc`}
        </span>
      ),
    },
    {
      key: "expiryDateTime",
      label: "Expiry Date",
      sortable: true,
      render: (_, row) => (
        <span className="text-sm text-gray-500">
          {formatForDisplay(row.expiryDateTime, "MM/DD/YYYY hh:mm a")}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      render: (_, row) => (
        <span className="text-sm text-gray-500">
          {formatForDisplay(row.createdAt, "MM/DD/YYYY hh:mm a")}
        </span>
      ),
    },
    {
      key: "attachments",
      label: "Attachments",
      sortable: false,
      render: (_, row) => (
        <span className="text-sm text-gray-500">
          {row.documentRequired ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (_, row) => (
        <div
          className="flex items-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          {row.status?.trim().toUpperCase() === "IN_PROGRESS" && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleResendClick(row);
              }}
              className="text-primary hover:text-primary-dark transition-colors duration-200"
              title="Resend Notification"
            >
              <ArrowPathIcon className="h-5 w-5" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDetailsClick(row);
            }}
            className="text-gray-600 hover:text-gray-900 transition-colors duration-200"
            title="View Details"
          >
            <DocumentMagnifyingGlassIcon className="h-5 w-5" />
          </button>
          {row.status !== "ARCHIVED" && (
            <PermissionGuard module="documents" action="delete">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(row);
                }}
                className="text-danger hover:opacity-90 transition-colors duration-200"
                title="Delete Document"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </PermissionGuard>
          )}
        </div>
      ),
    },
  ];

  const data = signRequests.map((r) => ({ ...r, id: r._id }));

  return (
    <PagePermissionGuard module="documents" action="read" pageName="Documents">
      <>
        {/* DataGrid - inside parent table container */}
        <DataGrid
          columns={columns}
          data={data}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          headerVariant="white"
          emptyMessage="No documents found. Create your first document from the Create Document tab."
          emptyIllustration={EmptyDocumentsIllustration}
          onRowClick={handleDetailsClick}
          className="!rounded-none !shadow-none"
          minHeight={480}
        />
        {/* Pagination */}
        {signRequests.length > 0 && (
          <div className="">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
        <DocumentDetailsSidebar
          isOpen={isSidebarOpen}
          onClose={handleCloseSidebar}
          document={selectedDocument}
          onDocumentUpdate={handleDocumentUpdated}
        />
      </>
    </PagePermissionGuard>
  );
};

export default AllDocuments;
